import React from 'react'
import { Search, Clock, Zap, CheckCircle, Sparkles } from 'lucide-react'

const Solution: React.FC = () => {
  const features = [
    {
      icon: Search,
      title: '글로벌 모니터링',
      items: ['국내외 유명 커뮤니티', '국내외 유명 IT 리더'],
      color: 'from-blue-500 to-blue-600',
      bgColor: 'from-blue-50 to-blue-100'
    },
    {
      icon: Clock,
      title: '5분 완독',
      items: ['핵심 요약', '트렌드 의견 추가'],
      color: 'from-green-500 to-green-600',
      bgColor: 'from-green-50 to-green-100'
    }
  ]

  return (
    <section className="section-padding bg-gradient-to-br from-white via-primary-50/30 to-purple-50/30 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-br from-primary-200/20 to-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-72 h-72 bg-gradient-to-tr from-green-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary-100/10 to-purple-100/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container-max relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 rounded-full px-4 py-2 mb-6 fade-in-up">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">해결책</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 fade-in-up">
            매주 AI 트렌드 추적
            <br />
            <span className="gradient-text">핵심 요약</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto fade-in-up" style={{animationDelay: '0.2s'}}>
            복잡한 AI 정보를 <span className="font-bold text-primary-700">5분만에</span> 핵심만 골라서 전달합니다
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-10">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`group bg-gradient-to-br ${feature.bgColor} p-8 rounded-3xl border border-white/50 hover:border-white/80 transition-all duration-500 hover:scale-105 hover:shadow-2xl fade-in-left`}
                style={{animationDelay: `${0.4 + index * 0.3}s`}}
              >
                <div className="flex items-start space-x-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors">
                      {feature.title}
                    </h3>
                    <ul className="space-y-3">
                      {feature.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-center text-gray-700 text-lg font-medium">
                          <div className={`w-3 h-3 bg-gradient-to-br ${feature.color} rounded-full mr-4 group-hover:scale-125 transition-transform duration-300`}></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center fade-in-right" style={{animationDelay: '0.8s'}}>
            <div className="relative">
              {/* Main card */}
              <div className="bg-white/80 backdrop-blur-sm p-12 rounded-3xl shadow-2xl border border-white/50 hover:shadow-3xl transition-all duration-500 hover:scale-105">
                <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-8 pulse-glow">
                  <Zap className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-3xl font-black text-gray-900 mb-6">
                  진짜 AI 최전선을 만나세요
                </h3>
                <p className="text-gray-700 text-xl leading-relaxed mb-8">
                  매주 업데이트되는 AI 트렌드의 핵심을
                  <br />
                  <span className="font-bold text-primary-700">빠르고 정확하게</span> 전달합니다
                </p>
                
                {/* Feature highlights */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-4 rounded-2xl">
                    <div className="text-2xl font-bold text-primary-700 mb-1">30분</div>
                    <div className="text-sm text-primary-600 font-medium">→ 3분</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl">
                    <div className="text-2xl font-bold text-purple-700 mb-1">10배</div>
                    <div className="text-sm text-purple-600 font-medium">효율성</div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-bounce"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-br from-green-400 to-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.5s'}}></div>
              <div className="absolute top-1/2 -left-8 w-4 h-4 bg-gradient-to-br from-pink-400 to-red-500 rounded-full animate-bounce" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20 fade-in-up" style={{animationDelay: '1.4s'}}>
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            <Sparkles className="w-6 h-6" />
            <span>지금 바로 시작하세요</span>
            <Sparkles className="w-6 h-6" />
          </div>
        </div>
      </div>
    </section>
  )
}

export default Solution
